import { subscribeNewsletter } from "api/newsletter";
import NewsletterCheckboxField from "components/inputs/CheckboxField/Newsletter";
import RequiredFieldsMessage from "components/RequiredFields/RequiredFieldsMessage";
import Terms from "components/Terms";
import { Formik, Form as FormikForm, FormikProps } from "formik";
import useGoogleRecaptcha from "hooks/useGoogleRecaptcha";
import {
  Button,
  errorToast,
  successToast,
  warningToast,
} from "iparque-components";
import { AppContext } from "providers/AppProvider";
import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { initialValues, validationSchema } from "./configs";
import { NewsletterFormValues } from "./interfaces";
import {
  CheckboxContainer,
  FormFields,
  StyledHeading,
  StyledInput,
} from "./styles";

const Form: React.FC = () => {
  const { t } = useTranslation();
  const { setIsLoading } = useContext(AppContext);
  const formRef = useRef<FormikProps<NewsletterFormValues>>(null);
  const { executeGoogleRecaptcha, isGoogleRecaptchaLoaded } =
    useGoogleRecaptcha();

  const onSubmitHandler = async (values: NewsletterFormValues) => {
    setIsLoading(true);

    if (!values.newsletters) {
      setIsLoading(false);
      warningToast(
        t("7857") /* Aviso! */,
        t(
          "13991"
        ) /* Para continuar a subscrição, deve seleccionar pelo menos uma newsletter */
      );
      return;
    }

    const token = await executeGoogleRecaptcha();

    if (!token) {
      setIsLoading(false);
      warningToast(
        t("7857") /* Aviso! */,
        t("7858") /* Por favor, tenta novamente. */
      );
      return;
    }

    const { success } = await subscribeNewsletter(values, token);

    setIsLoading(false);

    if (success) {
      formRef.current?.resetForm();
      successToast(
        t("586") /* Sucesso */,
        t("7926") /* A subscrição da newsletter foi realizada com sucesso */
      );
    } else {
      errorToast(
        t("7847") /* Erro! */,
        t("13985") /* Ocorreu um erro ao subscrever à newsletter */
      );
    }
  };

  return (
    <>
      <StyledHeading variant="h5" elementType="h3">
        {t("9241") /* Não fique fora do mapa! */}
      </StyledHeading>
      <StyledHeading variant="h6" elementType="h4" color="quaternary">
        {t("9242") /* Subscreva a nossa newsletter e entre na rota. */}
      </StyledHeading>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {(formikProps) => {
          const hasErrors =
            formikProps.touched &&
            Object.keys(formikProps.errors).length > 0 &&
            Object.keys(formikProps.touched).length > 0;

          return (
            <>
              <FormikForm>
                <FormFields>
                  <StyledInput
                    type="email"
                    id="newsletter-email-field"
                    name="email"
                    placeholder={t("178") /* Email */}
                    label={t("178") /* Email */}
                  />
                  <Button
                    type="submit"
                    color="secondary"
                    disabled={!isGoogleRecaptchaLoaded}
                  >
                    {t("3961") /* Subscrever */}
                  </Button>
                </FormFields>
                <CheckboxContainer>
                  <Terms />
                  <NewsletterCheckboxField
                    id="newsletters-field"
                    name="newsletters"
                    required
                    label={
                      t(
                        "14370"
                      ) /* Concordo em receber newsletters de campanhas publicitárias promovidas pela ACIN – iCloud Solutions, Lda.  */
                    }
                  />
                </CheckboxContainer>
              </FormikForm>
              <RequiredFieldsMessage hasErrors={hasErrors} />
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default Form;
